import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';

export interface IQRCodeGenerateRequest {
	type: 'generate';
	data: string;
	pixelsPerModule?: number;
}

export interface IQRCodeUrlRequest {
	type: 'url';
	baseUrl: string;
	path?: string;
	pixelsPerModule?: number;
}

export type IQRCodeRequest = IQRCodeGenerateRequest | IQRCodeUrlRequest;

export const useQRCodeMutation = () => {
	const userSession = useUserSession();
	const impersonationContext = userSession?.impersonationContext;

	return useMutation<string, Api.IOperationResultNoValue, IQRCodeRequest>({
		mutationFn: request => {
			let params: URLSearchParams;
			let endpoint: string;

			if (request.type === 'generate') {
				params = new URLSearchParams({
					data: request.data,
					pixelsPerModule: (request.pixelsPerModule ?? 20).toString(),
				});
				endpoint = 'qrcode/generate';
			} else {
				params = new URLSearchParams({
					baseUrl: request.baseUrl,
					pixelsPerModule: (request.pixelsPerModule ?? 20).toString(),
				});
				if (request.path) {
					params.set('path', request.path);
				}
				endpoint = 'qrcode/url';
			}

			return userSession.webServiceHelper.callAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `${endpoint}?${params.toString()}`,
				}),
				'GET'
			);
		},
	});
};

// Convenience hook specifically for event check-in QR codes
export const useEventCheckInQRCodeMutation = () => {
	const qrMutation = useQRCodeMutation();

	return {
		...qrMutation,
		generateEventCheckInQR: (params: {
			baseUrl: string;
			eventId: string;
			confirmationNumber: string;
			pixelsPerModule?: number;
		}) => {
			const path = `public/eventRegistration/${params.eventId}/checkin/${params.confirmationNumber}`;
			return qrMutation.mutateAsync({
				type: 'url',
				baseUrl: params.baseUrl,
				path,
				pixelsPerModule: params.pixelsPerModule,
			});
		},
	};
};
